import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  createWorkspace,
  getAllWorkspaces,
  updatePetFormula,
} from "../api/internal_tool_endpoints";

// Async thunk to create workspace
export const createWorkspaceAction = createAsyncThunk(
  "workspace/createWorkspace",
  async ({ id_client, workspace_info }, { rejectWithValue }) => {
    try {
      const result = await createWorkspace({ id_client, workspace_info });
      return result;
    } catch (error) {
      console.error("🚀 Failed to create workspace:", error);
      // `status` lets Create Workspace distinguish a server rejection from an
      // unknown outcome; consumers read `.message` for display.
      return rejectWithValue({
        message: error.message,
        status: error.status,
      });
    }
  }
);

// Async thunk to add a new market (country) to an existing workspace
export const updatePetFormulaAction = createAsyncThunk(
  "workspace/updatePetFormula",
  async ({ id_client, formula_update }, { rejectWithValue }) => {
    try {
      const result = await updatePetFormula({ id_client, formula_update });
      return result;
    } catch (error) {
      console.error("🚀 Failed to update pet formula:", error);
      // `status` lets Add Market distinguish a server rejection from an
      // unknown outcome; consumers read `.message` for display.
      return rejectWithValue({
        message: error.message,
        status: error.status,
      });
    }
  }
);

// Async thunk to get all workspaces
export const fetchAllWorkspacesAction = createAsyncThunk(
  "workspace/fetchAllWorkspaces",
  async (_, { rejectWithValue }) => {
    try {
      const result = await getAllWorkspaces();
      return result;
    } catch (error) {
      console.error(
        "🚀 [workspaceSlice] Failed to fetch all workspaces:",
        error
      );
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Build hierarchical workspace data structure: id_client -> region -> markets
 * @param {Array} workspaces - Raw workspace array from API
 * @returns {Object} Hierarchical structure { [id_client]: { regions: { [region]: { markets: [...] } } } }
 */
const buildHierarchicalWorkspaces = (workspaces) => {
  if (!workspaces || workspaces.length === 0) return {};

  return workspaces.reduce((acc, workspace) => {
    const clientId = workspace.id_client;
    const region = workspace.region || "Unknown Region";
    const markets = workspace.markets || [];

    // Initialize client if not exists
    if (!acc[clientId]) {
      acc[clientId] = {
        id_client: clientId,
        mapped_id_client: workspace.mapped_id_client,
        regions: {},
      };
    }

    // Initialize region if not exists
    if (!acc[clientId].regions[region]) {
      acc[clientId].regions[region] = {
        regionName: region,
        markets: [],
      };
    }

    // Add markets (deduplicate)
    markets.forEach((market) => {
      if (!acc[clientId].regions[region].markets.includes(market)) {
        acc[clientId].regions[region].markets.push(market);
      }
    });

    return acc;
  }, {});
};

/**
 * Convert hierarchical data to regionMarketData format for compatibility
 * Format: { [regionName]: { markets: [...], scopes: {...} } }
 * @param {Object} hierarchicalData - Hierarchical workspace data
 * @param {string} clientId - Target client ID (optional, if not provided, merge all)
 * @returns {Object} regionMarketData format
 */
const buildRegionMarketData = (hierarchicalData, clientId = null) => {
  const result = {};

  const clientIds = clientId ? [clientId] : Object.keys(hierarchicalData);

  clientIds.forEach((cId) => {
    const client = hierarchicalData[cId];
    if (!client) return;

    Object.entries(client.regions).forEach(([regionName, regionData]) => {
      if (!result[regionName]) {
        result[regionName] = {
          markets: [],
          scopes: {},
        };
      }

      regionData.markets.forEach((market) => {
        if (!result[regionName].markets.includes(market)) {
          result[regionName].markets.push(market);
          // Default scope for each market
          result[regionName].scopes[market] =
            "Deepdive; Execution view; Market Overview";
        }
      });
    });
  });

  return result;
};

const initialState = {
  loading: false,
  error: null,
  success: false,
  lastCreatedWorkspace: null,

  // Add-market (pet_formula update) state — kept separate from the create flow
  // so an open Add Market wizard never reads the create wizard's status.
  addMarketLoading: false,
  addMarketError: null,
  addMarketSuccess: false,

  // All workspaces data with metadata
  allWorkspaces: [],
  totalClients: 0,
  totalRecords: 0,
  fetchingWorkspaces: false,
  fetchWorkspacesError: null,

  // Hierarchical workspace data: { [id_client]: { regions: { [region]: { markets: [...] } } } }
  hierarchicalWorkspaces: {},
  // Region-market format for UI compatibility: { [region]: { markets: [...], scopes: {...} } }
  regionMarketData: {},
};

const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    // Clear workspace creation state
    clearWorkspaceState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.lastCreatedWorkspace = null;
    },

    // Clear all workspaces data
    clearAllWorkspacesState: (state) => {
      state.allWorkspaces = [];
      state.totalClients = 0;
      state.totalRecords = 0;
      state.fetchingWorkspaces = false;
      state.fetchWorkspacesError = null;
      state.hierarchicalWorkspaces = {};
      state.regionMarketData = {};
    },

    // Reset only success state (useful after showing success message)
    resetSuccessState: (state) => {
      state.success = false;
    },

    // Clear add-market state
    clearAddMarketState: (state) => {
      state.addMarketLoading = false;
      state.addMarketError = null;
      state.addMarketSuccess = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Add market to existing workspace (pet_formula update)
      .addCase(updatePetFormulaAction.pending, (state) => {
        state.addMarketLoading = true;
        state.addMarketError = null;
        state.addMarketSuccess = false;
      })
      .addCase(updatePetFormulaAction.fulfilled, (state) => {
        state.addMarketLoading = false;
        state.addMarketSuccess = true;
        state.addMarketError = null;
      })
      .addCase(updatePetFormulaAction.rejected, (state, action) => {
        state.addMarketLoading = false;
        state.addMarketError = action.payload?.message ?? action.payload;
        state.addMarketSuccess = false;
      })
      // Create workspace
      .addCase(createWorkspaceAction.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createWorkspaceAction.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.lastCreatedWorkspace = action.payload;
      })
      .addCase(createWorkspaceAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message ?? action.payload;
        state.success = false;
      })
      // Fetch all workspaces
      .addCase(fetchAllWorkspacesAction.pending, (state) => {
        state.fetchingWorkspaces = true;
        state.fetchWorkspacesError = null;
      })
      .addCase(fetchAllWorkspacesAction.fulfilled, (state, action) => {
        state.fetchingWorkspaces = false;
        state.fetchWorkspacesError = null;

        // Store the structured data from the API response
        const { workspaces, totalClients, totalRecords } = action.payload;
        state.allWorkspaces = workspaces;
        state.totalClients = totalClients;
        state.totalRecords = totalRecords;

        // Build hierarchical data structure: id_client -> region -> markets
        state.hierarchicalWorkspaces = buildHierarchicalWorkspaces(workspaces);
        // Build region-market format for UI compatibility
        state.regionMarketData = buildRegionMarketData(
          state.hierarchicalWorkspaces
        );

        console.log("🔄 [workspaceSlice] Updated allWorkspaces state:", {
          workspaces: workspaces.length,
          totalClients,
          totalRecords,
          hierarchicalWorkspaces: Object.keys(state.hierarchicalWorkspaces)
            .length,
          regionMarketData: Object.keys(state.regionMarketData).length,
        });
      })
      .addCase(fetchAllWorkspacesAction.rejected, (state, action) => {
        state.fetchingWorkspaces = false;
        state.fetchWorkspacesError = action.payload;
      });
  },
});

export const {
  clearWorkspaceState,
  clearAllWorkspacesState,
  resetSuccessState,
  clearAddMarketState,
} = workspaceSlice.actions;

// Export helper functions for use in components
export { buildHierarchicalWorkspaces, buildRegionMarketData };

export default workspaceSlice.reducer;
